/**
 * The topic a support/feedback note is about — shared by the PUBLIC contact form
 * (`src/feedback/**`, `POST /feedback`) and the AUTHENTICATED request flow (`src/support/**`,
 * `POST /support/request`), because "what is this about?" is one question with one answer set no
 * matter which of the two dialogs the sender happened to reach (#520 merged the ENTRY; this merges
 * the vocabulary behind it).
 *
 * Pure TypeScript with no React import (epic #218 "logic outside React"): the composition rule and
 * the length arithmetic below are the whole reason this module exists, and both must be testable
 * without a renderer.
 *
 * ## Why the topic rides INSIDE the message
 *
 * Neither endpoint has a field for it. `FeedbackRequest` is `{message, replyTo?}` and nothing more,
 * and `SupportRequest`'s `category` is a CLOSED api enum (`GENERAL` | `EU_WITHDRAWAL`) whose
 * `EU_WITHDRAWAL` value triggers a statutory acknowledgement — overloading it with product topics
 * would either lose the topic or fire that acknowledgement, so it is deliberately left alone.
 *
 * A tagged first line is therefore the only shipping-today option, and it is a UI-SIDE convention:
 * the api's own `[PREMIUM] [PRIORITY]` markers are resolved SERVER-side from the entitlement
 * catalogue and are not a body protocol this piggybacks on. A real `topic` field on both requests
 * would be strictly better — it would survive a sender who types the same bracket text themselves,
 * and it would be queryable in the inbox — and is filed as follow-up work.
 */

/**
 * The offered topics, in the order the select presents them. The owner named the first four
 * (2026-08-16); the last three come from the same request's "you add other options".
 *
 * The ORDER is the product decision, so it lives here rather than in the catalog: a translator
 * reordering a copy object must not silently reorder the control.
 */
export const FEEDBACK_TOPICS = [
	"featureRequest",
	"bugReport",
	"reportIssue",
	"humanSupport",
	"billing",
	"translation",
	"other",
] as const;

/** One of {@link FEEDBACK_TOPICS}. */
export type FeedbackTopic = (typeof FEEDBACK_TOPICS)[number];

/**
 * What the support inbox actually reads, per topic.
 *
 * ENGLISH and STABLE on purpose: this is an api-facing routing marker, not user-visible copy, so it
 * does not belong in the i18n catalog (a Romanian sender's note must land in the same inbox bucket as
 * an English one, and a translator must not be able to change how mail is triaged). It is the same
 * class of value as an enum name or an API field name, which the repo's copy rule excludes by name.
 */
const FEEDBACK_TOPIC_TAGS: Record<FeedbackTopic, string> = {
	featureRequest: "[FEATURE REQUEST]",
	bugReport: "[BUG REPORT]",
	reportIssue: "[REPORTED ISSUE]",
	humanSupport: "[HUMAN SUPPORT]",
	billing: "[BILLING]",
	translation: "[TRANSLATION]",
	other: "[OTHER]",
};

/** The inbox marker for one topic. */
export function feedbackTopicTag(topic: FeedbackTopic): string {
	return FEEDBACK_TOPIC_TAGS[topic];
}

/** Whether an arbitrary string is a topic — the guard a `<select>` value has to pass to be stored. */
export function isFeedbackTopic(value: string): value is FeedbackTopic {
	return (FEEDBACK_TOPICS as readonly string[]).includes(value);
}

/**
 * The message body as it should be POSTED.
 *
 * `null` — the sender chose no topic — posts the note completely UNCHANGED. That is what makes the
 * select safe to add to two shipping forms: leaving it alone reproduces today's request byte for
 * byte, so the topic can only ever add information, never alter a note that was already fine. It is
 * also why no topic is pre-selected: a default would silently label every note that ignored the
 * control, and mislabelled triage is worse than untriaged.
 *
 * @example
 * tagMessageWithTopic("bugReport", "The upload stalls at 90%.");
 * // => "[BUG REPORT]\nThe upload stalls at 90%."
 */
export function tagMessageWithTopic(topic: FeedbackTopic | null, message: string): string {
	return topic === null ? message : `${feedbackTopicTag(topic)}\n${message}`;
}

/**
 * How many characters of `max` the sender may actually type once the tag is prepended.
 *
 * Load-bearing rather than cosmetic. Both endpoints bound the message they RECEIVE (`@Size(max=512)`
 * on feedback, `@Size(max=2000)` on support), and what they receive is the tagged string. Validating
 * and counting against the raw `max` would let a sender fill the field to exactly the limit, then
 * meet a 400 that no retry could clear — the precise dead end ui#326 removed from this form. So the
 * budget shrinks by the tag and its newline, the counter shows the smaller number, and the field's
 * own `maxLength` stops typing there.
 *
 * The budget is in CHARACTERS, which is the whole story for `/feedback` (`@Size` and nothing else) but
 * not for `/support/request`, whose message also carries `@MaxUtf8Bytes(max = 2000)`. That second
 * bound is unmirrored on this side and predates this module — every tag here is ASCII, so it costs
 * chars and bytes alike and cannot widen the gap — but it does mean a support message of >2000 UTF-8
 * bytes within 2000 characters (Romanian diacritics, emoji) is still refused by the api rather than at
 * the field. The byte-aware form of exactly this arithmetic already exists in
 * `account/withdrawal/withdrawalSubmission.ts`; lifting it here is filed as follow-up work.
 *
 * @example
 * maxMessageLengthForTopic("bugReport", 512); // => 499  (512 - "[BUG REPORT]".length - 1)
 */
export function maxMessageLengthForTopic(topic: FeedbackTopic | null, max: number): number {
	return topic === null ? max : max - feedbackTopicTag(topic).length - 1;
}
